import { OverflowList } from '@theme/overflow-list';
import VariantPicker from '@theme/variant-picker';
import { Component } from '@theme/component';
import { debounce, isDesktopBreakpoint, mediaQueryLarge, yieldToMainThread } from '@theme/utilities';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent, SlideshowSelectEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { isSaved, toggleSaved, WISHLIST_CHANGE } from '@theme/custom.wishlist';

/**
 * @typedef {object} ProductCardLinkRefs
 * @property {HTMLElement} [cardGallery] - The card gallery element.
 * @property {HTMLImageElement[]} [imagesToTransition] - The images to transition.
 */

/**
 * A custom element for product links with images for transitions to PDP.
 * This is a base class that is extended by ProductCard.
 * Used directly by resource-card.liquid for non-product-card scenarios.
 *
 * @template {ProductCardLinkRefs} [T=ProductCardLinkRefs]
 * @extends {Component<T>}
 */
export class ProductCardLink extends Component {
  get productTransitionEnabled() {
    return this.getAttribute('data-product-transition') === 'true';
  }

  get featuredMediaUrl() {
    return this.getAttribute('data-featured-media-url');
  }

  /**
   * Handles the click event for view transitions.
   * @param {Event} event
   */
  handleViewTransition(event) {
    // If the event has been prevented, don't do anything, another component is handling the click
    if (event.defaultPrevented) return;

    // If the event was on an interactive element, don't do anything, this is not a navigation
    if (event.target instanceof Element) {
      const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');
      if (interactiveElement) return;
    }

    if (!this.productTransitionEnabled) return;

    const { cardGallery } = this.refs;
    if (!cardGallery || !cardGallery.hasAttribute('data-view-transition-to-main-product')) return;

    // Check on the current active image, whether it's a product card image or a resource card image
    const { imagesToTransition } = this.refs;
    const activeImage =
      imagesToTransition?.find(
        (/** @type {HTMLImageElement} */ image) =>
          image.closest('slideshow-slide')?.matches(':not([data-offscreen])')
      ) || imagesToTransition?.[imagesToTransition.length - 1];

    if (activeImage instanceof HTMLImageElement) this.#setImageSrcset(activeImage);

    cardGallery.setAttribute('data-view-transition-type', 'product-image-transition');
    cardGallery.setAttribute('data-view-transition-triggered', 'true');
  }

  /**
   * Sets the srcset for the image
   * @param {HTMLImageElement} image
   */
  #setImageSrcset(image) {
    if (!this.featuredMediaUrl) return;

    const currentImageUrl = new URL(image.currentSrc);

    // Deliberately not using origin, as it includes the protocol, which is usually skipped for featured media
    const currentImageRawUrl = currentImageUrl.host + currentImageUrl.pathname;

    if (!this.featuredMediaUrl.includes(currentImageRawUrl)) {
      const imageFade = image.animate([{ opacity: 0.8 }, { opacity: 1 }], {
        duration: 125,
        easing: 'ease-in-out',
      });

      imageFade.onfinish = () => {
        image.srcset = this.featuredMediaUrl ?? '';
      };
    }
  }
}

if (!customElements.get('product-card-link')) {
  customElements.define('product-card-link', ProductCardLink);
}

/**
 * A custom element that displays a product card.
 * Extends ProductCardLink to inherit view transition functionality.
 *
 * @typedef {object} ProductCardRefs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {import('slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {import('quick-add').QuickAddComponent} [quickAdd] - The quick add component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 * @property {HTMLImageElement[]} [imagesToTransition] - The images to transition.
 * @extends {ProductCardLink<ProductCardRefs>}
 */
export class ProductCard extends ProductCardLink {
  requiredRefs = ['productCardLink'];

  get productPageUrl() {
    return this.refs.productCardLink.href;
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null if none selected
   */
  getSelectedVariantId() {
    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );

    return checkedInput?.dataset.variantId || null;
  }

  /**
   * Gets the product card link element
   * @returns {HTMLAnchorElement | null} The product card link or null
   */
  getProductCardLink() {
    return this.refs.productCardLink || null;
  }

  #fetchProductPageHandler = () => {
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);
  };

  /**
   * Navigates to a URL link. Respects modifier keys for opening in new tab/window.
   * @param {Event} event - The event that triggered the navigation.
   * @param {URL} url - The URL to navigate to.
   */
  #navigateToURL = (event, url) => {
    // Check for modifier keys that should open in new tab/window (only for mouse events)
    const shouldOpenInNewTab =
      event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, '_blank');
      return;
    } else {
      window.location.href = url.href;
    }
  };

  connectedCallback() {
    super.connectedCallback();

    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement)) throw new Error('Product card link not found');
    this.#handleQuickAdd();

    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    mediaQueryLarge.addEventListener('change', this.#handleQuickAdd);
    this.addEventListener('pointerenter', this.#engage);
    this.addEventListener('scroll', this.#engage, { capture: true });

    this.addEventListener('click', this.navigateToProduct);

    // The heart persists in a store shared with the PDP, so it has to be reflected rather than
    // assumed empty, or a saved product comes back unsaved in the grid.
    this.reflectFavorite();
    document.addEventListener(WISHLIST_CHANGE, this.reflectFavorite);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.navigateToProduct);
    this.removeEventListener('pointerenter', this.#engage);
    this.removeEventListener('scroll', this.#engage, { capture: true });
    document.removeEventListener(WISHLIST_CHANGE, this.reflectFavorite);
  }

  updatedCallback() {
    super.updatedCallback();
    // Morph reuses this card, so `connectedCallback` does not rerun, and the server markup says
    // `aria-pressed="false"`. Without this a saved heart would show empty after a filter or sort.
    this.reflectFavorite();
  }

  /**
   * Paints every heart in this card from the shared wishlist.
   */
  reflectFavorite = () => {
    const saved = String(isSaved(this.dataset.productId));

    for (const fav of this.querySelectorAll('.product-card__fav')) {
      fav.setAttribute('aria-pressed', saved);
    }
  };

  /**
   * Lets the photos behind the current one load (card-gallery gates them). A hover preview engages
   * once its photo is in, so the others do not share its fetch. Not once: a morph drops it.
   * @param {Event} event
   */
  #engage = (event) => {
    const hoverPreview = this.refs.cardGallery?.hasAttribute('on:pointerenter');
    if (event instanceof PointerEvent && (event.pointerType !== 'mouse' || hoverPreview)) return;
    this.toggleAttribute('data-engaged', true);
  };

  /**
   * The slide the card shows when no one is hovering it: the picked swatch's photo, else its first.
   * @returns {HTMLElement | undefined}
   */
  #restSlide() {
    const { slideshow } = this.refs;
    const slides = slideshow?.refs.slides ?? [];
    const pickedId = this.variantPicker?.selectedOption?.dataset.optionMediaId;
    const picked = pickedId ? slides.find((slide) => slide.getAttribute('slide-id') === pickedId) : undefined;
    const initial = slideshow?.initialSlide;

    return picked ?? (initial && !initial.hidden ? initial : slides.find((slide) => !slide.hidden));
  }

  /**
   * The slide a hover shows: the one after the rest slide, past the variant photos it hides.
   * @param {HTMLElement | undefined} rest
   * @returns {HTMLElement | undefined}
   */
  #previewSlide(rest) {
    const shown = (this.refs.slideshow?.refs.slides ?? []).filter((slide) => !slide.hidden);
    const preview = shown[((rest ? shown.indexOf(rest) : -1) + 1) % shown.length];

    return preview === rest ? undefined : preview;
  }

  /**
   * Starts a slide's photo at the width it paints: `sizes="auto"` applies to a lazy image only.
   * @param {HTMLElement} slide
   * @returns {HTMLImageElement | null}
   */
  #loadPreviewImage(slide) {
    const image = slide.querySelector('img');
    if (!(image instanceof HTMLImageElement)) return null;

    // Only a photo no fetch has picked a source for: resizing one mid-fetch fetches twice. Firefox
    // never fetches a lazy photo beside the scroller's view, and WebKit calls a gated one complete.
    if (!image.currentSrc && image.loading === 'lazy') {
      const box = slide.getBoundingClientRect();
      const aspect = Number(image.getAttribute('width')) / Number(image.getAttribute('height')) || 0;
      // A cropped frame paints the photo at the frame height, wider than the box; a contained one
      // fits it inside, no wider.
      const fit = getComputedStyle(image).objectFit === 'contain' ? Math.min : Math.max;
      image.sizes = `${Math.ceil(fit(box.width, box.height * aspect) || box.width)}px`;
      image.loading = 'eager';
    }
    return image;
  }

  /**
   * Shows a slide by id, without the scroll animation a hover cannot wait for.
   * @param {HTMLElement | undefined} slide
   */
  #show(slide) {
    const id = slide?.getAttribute('slide-id');
    if (id) this.refs.slideshow?.select({ id }, undefined, { animate: false });
  }

  /**
   * Handles the quick add event.
   */
  #handleQuickAdd = () => {
    this.removeEventListener('pointerenter', this.#fetchProductPageHandler);
    this.removeEventListener('focusin', this.#fetchProductPageHandler);

    if (isDesktopBreakpoint()) {
      this.addEventListener('pointerenter', this.#fetchProductPageHandler);
      this.addEventListener('focusin', this.#fetchProductPageHandler);
    }
  };

  /**
   * Handles the variant selected event.
   * @param {VariantSelectedEvent} event - The variant selected event.
   */
  #handleVariantSelected = (event) => {
    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(event.detail.resource.id);
    }
  };

  /**
   * Handles the variant update event.
   * Updates price, checks for unavailable variants, and updates product URL.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    // Stop the event from bubbling up to the section, variant updates triggered from product cards are fully handled
    // by this component and should not affect anything outside the card.
    event.stopPropagation();

    this.updatePrice(event);
    this.#isUnavailableVariantSelected(event);
    this.#updateProductUrl(event);
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);

    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateVariantPicker(event.detail.data.html);
    }

    this.#updateVariantImages();
    this.#previousSlideId = null;

    // Remove attribute after re-rendering since a variant selection has been made
    this.removeAttribute('data-no-swatch-selected');

    // Force overflow list to reflow after variant update
    // This fixes an issue where the overflow counter doesn't update properly in some browsers
    this.#updateOverflowList();
  };

  /**
   * Forces the overflow list to recalculate by dispatching a reflow event.
   * This ensures the overflow counter displays correctly after variant updates.
   */
  #updateOverflowList() {
    // Find the overflow list in the variant picker
    const overflowList = this.querySelector('swatches-variant-picker-component overflow-list');
    const isActiveOverflowList = overflowList?.querySelector('[slot="overflow"]') ? true : false;
    if (!overflowList || !isActiveOverflowList) return;

    // Use requestAnimationFrame to ensure DOM has been updated
    requestAnimationFrame(() => {
      // Dispatch a reflow event to trigger recalculation
      overflowList.dispatchEvent(
        new CustomEvent('reflow', {
          bubbles: true,
          detail: {},
        })
      );
    });
  }

  /**
   * Updates the DOM with a new price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice(event) {
    const newPriceElement = event.detail.data.html.querySelector(`product-price [ref='priceContainer']`);
    if (!newPriceElement) return;

    for (const priceContainer of this.querySelectorAll(`product-price [ref='priceContainer']`)) {
      // A card can have one or several price blocks. Morph may move nodes out of its source.
      morph(priceContainer, newPriceElement.cloneNode(true));
    }
  }

  /**
   * Updates the product URL based on the variant update event.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #updateProductUrl(event) {
    const responseProductCard = event.detail.data.html?.querySelector('product-card');
    const anchorElement = responseProductCard?.querySelector('a');
    const featuredMediaUrl = responseProductCard?.getAttribute('data-featured-media-url');

    // Update the featured media URL for view transitions (inherited from ProductCardLink)
    if (featuredMediaUrl) {
      this.setAttribute('data-featured-media-url', featuredMediaUrl);
    }

    if (anchorElement instanceof HTMLAnchorElement) {
      // If the href is empty, don't update the product URL eg: unavailable variant
      if (anchorElement.getAttribute('href')?.trim() === '') return;

      const productUrl = anchorElement.href;
      const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;

      productCardLink.href = productUrl;
      if (cardGalleryLink instanceof HTMLAnchorElement) {
        cardGalleryLink.href = productUrl;
      }
      if (productTitleLink instanceof HTMLAnchorElement) {
        productTitleLink.href = productUrl;
      }
    }
  }

  /**
   * Checks if an unavailable variant is selected.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #isUnavailableVariantSelected(event) {
    const allVariants = /** @type {NodeListOf<HTMLInputElement>} */ (
      event.detail.data.html.querySelectorAll('input:checked')
    );

    for (const variant of allVariants) {
      this.#toggleAddToCartButton(variant.dataset.optionAvailable === 'true');
    }
  }

  /**
   * Toggles the add to cart button state.
   * @param {boolean} enable - Whether to enable or disable the button.
   */
  #toggleAddToCartButton(enable) {
    const addToCartButton = this.querySelector('.add-to-cart__button button');

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enable;
    }
  }

  /**
   * Hide the variant images that are not for the selected variant.
   */
  #updateVariantImages() {
    const { slideshow } = this.refs;
    if (!this.variantPicker?.selectedOption) {
      return;
    }

    const selectedImageId = this.variantPicker?.selectedOption.dataset.optionMediaId;

    if (slideshow && selectedImageId) {
      const { slides = [] } = slideshow.refs;
      // The card renders only the variant photos a swatch reaches (card-gallery). Hiding every one
      // for a photo it does not have would leave the card on a generic slide or none.
      if (!slides.some((slide) => slide.getAttribute('slide-id') === selectedImageId)) return;

      for (const slide of slides) {
        if (slide.getAttribute('variant-image') == null) continue;

        slide.hidden = slide.getAttribute('slide-id') !== selectedImageId;
      }

      slideshow.select({ id: selectedImageId }, undefined, { animate: false });
    }
  }

  /**
   * Gets all variant inputs.
   * @returns {NodeListOf<HTMLInputElement>} All variant input elements.
   */
  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  /**
   * Gets the variant picker component.
   * @returns {VariantPicker | null} The variant picker component.
   */
  get variantPicker() {
    return this.querySelector('swatches-variant-picker-component');
  }
  /** The slide a shopper moved the card to with its arrows, which a hover goes back to. */
  /** @type {string | null} */
  #previousSlideId = null;

  /** @type {AbortController | null} */
  #pendingPreview = null;

  /**
   * Handles the slideshow select event.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    if (event.detail.userInitiated) {
      this.#previousSlideId = event.detail.id;
    }
  };

  /**
   * Previews a variant.
   * @param {string} id - The id of the variant to preview.
   */
  previewVariant(id) {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();
    slideshow.select({ id }, undefined, { animate: false });
  }

  /**
   * Previews the next image.
   * @param {PointerEvent} event - The pointer event.
   */
  previewImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();
    this.#pendingPreview?.abort();
    this.#pendingPreview = null;

    const rest = this.#restSlide();
    const moved = slideshow.refs.slides?.find((slide) => slide.getAttribute('slide-id') === this.#previousSlideId);
    if (moved && moved !== rest && !moved.hidden) {
      this.#show(moved);
      return;
    }

    const preview = this.#previewSlide(rest);
    if (!preview) return;

    // The card keeps its painted slide until the hover photo is in; `complete` alone is also true
    // for a failed one.
    const image = this.#loadPreviewImage(preview);
    if (image && !(image.complete && image.naturalWidth)) {
      this.#pendingPreview = new AbortController();
      image.addEventListener(
        'load',
        () => {
          this.#pendingPreview = null;
          this.#show(preview);
          this.toggleAttribute('data-engaged', true);
        },
        { once: true, signal: this.#pendingPreview.signal }
      );
      // A failed hover photo stays off the card, but must not keep its other photos gated.
      image.addEventListener(
        'error',
        () => {
          this.#pendingPreview = null;
          this.toggleAttribute('data-engaged', true);
        },
        { once: true, signal: this.#pendingPreview.signal }
      );
      return;
    }

    this.#show(preview);
    this.toggleAttribute('data-engaged', true);
  }

  /**
   * Puts the card back on its rest slide.
   * @param {PointerEvent} event - The pointer event.
   */
  resetImage(event) {
    if (event.pointerType !== 'mouse') return;

    // The preview never switched, so there is nothing to undo.
    if (this.#pendingPreview) {
      this.#pendingPreview.abort();
      this.#pendingPreview = null;
      return;
    }

    this.#resetVariant();
  }

  /**
   * Puts the card back on its rest slide after a swatch or photo preview.
   */
  #resetVariant = () => {
    this.#show(this.#restSlide());
  };

  /**
   * Intercepts the click event on the product card anchor, we want
   * to use this to add an intermediate state to the history.
   * This intermediate state captures the page we were on so that we
   * navigate back to the same page when the user navigates back.
   * In addition to that, it captures the product card anchor so that we
   * have the specific product card in view.
   *
   * A product card can have other interactive elements like variant picker,
   * so we do not navigate if the click was on one of those elements.
   *
   * @param {Event} event
   */
  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) return;

    // Don't navigate if this product card is marked as no-navigation (e.g., in theme editor)
    if (this.hasAttribute('data-no-navigation')) return;

    const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');

    // If the click was on an interactive element, do nothing.
    if (interactiveElement) {
      return;
    }

    const link = this.refs.productCardLink;
    if (!link.href) return;
    const linkURL = new URL(link.href);

    const productCardAnchor = link.getAttribute('id');
    if (!productCardAnchor) return;

    // Scrolling mode only. Stamping the page is safe there because it back-fills the pages above
    // on return; Load more has no back-fill, so a stamped ?page=N would come back stranded.
    const infiniteResultsList = this.closest('results-list[pagination-mode="infinite"]');
    if (!window.Shopify.designMode && infiniteResultsList) {
      const url = new URL(window.location.href);
      const parent = this.closest('li');
      url.hash = productCardAnchor;
      if (parent && parent.dataset.page) {
        url.searchParams.set('page', parent.dataset.page);
      }

      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }

    const targetLink = event.target.closest('a');
    // Let the native navigation handle the click if it was on a link.
    if (!targetLink) {
      this.#navigateToURL(event, linkURL);
    }
  };

  /**
   * Toggles the saved state of the wishlist heart. Writes through the shared store, so both hearts
   * in this card (image overlay on mobile/tablet, below-image on desktop) and the PDP heart for the
   * same product all follow, via the change event rather than by being set here.
   * @param {Event} event
   */
  toggleFavorite(event) {
    const button = event.target instanceof Element ? event.target.closest('.product-card__fav') : null;
    if (!button) return;

    // Writes through the store the PDP heart also reads. The change event repaints this card
    // and any other heart for the same product, so no attribute is set by hand here.
    toggleSaved(this.dataset.productId);
  }

  /**
   * Resets the variant.
   */
  resetVariant = debounce(this.#resetVariant, 100);
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);

  // A mouse latches its section warm (card-gallery): a hover photo hidden again loses its
  // `sizes="auto"` width, and Chrome fetches it a second time at the viewport's.
  document.addEventListener(
    'pointerover',
    (event) => {
      if (event.pointerType !== 'mouse' || !(event.target instanceof Element)) return;
      event.target.closest('.shopify-section')?.toggleAttribute('data-cards-warm', true);
    },
    { passive: true }
  );
}

/**
 * A custom element that displays a variant picker with swatches.
 * @typedef {import('@theme/variant-picker').VariantPickerRefs & {overflowList: HTMLElement}} SwatchesRefs
 */

/**
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  connectedCallback() {
    super.connectedCallback();

    // Cache the parent product card
    this.parentProductCard = this.closest('product-card');

    // Listen for variant updates to apply pending URL changes
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleCardVariantUrlUpdate.bind(this));
  }

  /**
   * Updates the card URL when a variant is selected.
   */
  #handleCardVariantUrlUpdate() {
    if (this.pendingVariantId && this.parentProductCard instanceof ProductCard) {
      const currentUrl = new URL(this.parentProductCard.refs.productCardLink.href);
      currentUrl.searchParams.set('variant', this.pendingVariantId);
      this.parentProductCard.refs.productCardLink.href = currentUrl.toString();
      this.pendingVariantId = null;
    }
  }

  /**
   * Override the variantChanged method to handle unavailable swatches with available alternatives.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    // Check if this is a swatch input
    const isSwatchInput = event.target instanceof HTMLInputElement && event.target.name?.includes('-swatch');
    const clickedSwatch = event.target;
    const availableCount = parseInt(clickedSwatch.dataset.availableCount || '0');
    const firstAvailableVariantId = clickedSwatch.dataset.firstAvailableOrFirstVariantId;

    // For swatch inputs, check if we need special handling
    if (isSwatchInput && availableCount > 0 && firstAvailableVariantId) {
      // If this is an unavailable variant but there are available alternatives
      // Prevent the default handling
      event.stopPropagation();

      // Update the selected option visually
      this.updateSelectedOption(clickedSwatch);

      // Build request URL with the first available variant
      const productUrl = this.dataset.productUrl?.split('?')[0];

      if (!productUrl) return;

      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', firstAvailableVariantId);
      url.searchParams.set('section_id', 'section-rendering-product-card');

      const requestUrl = url.href;

      // Store the variant ID we want to apply to the URL
      this.pendingVariantId = firstAvailableVariantId;

      // Use parent's fetch method
      this.fetchUpdatedSection(requestUrl);
      return;
    }

    // For all other cases, use the default behavior
    super.variantChanged(event);
  }

  /**
   * Shows all swatches.
   * @param {Event} [event] - The event that triggered the show all swatches.
   */
  showAllSwatches(event) {
    event?.preventDefault();

    const { overflowList } = this.refs;

    if (overflowList instanceof OverflowList) {
      overflowList.showAll();
    }
  }
}

if (!customElements.get('swatches-variant-picker-component')) {
  customElements.define('swatches-variant-picker-component', SwatchesVariantPickerComponent);
}
